import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { List, X, Translate } from "@phosphor-icons/react";
import { useLanguage } from "@/i18n";

const SECTIONS = ["about", "properties", "reviews", "video", "faq", "contact"];

export const scrollToSection = (e, href) => {
  e.preventDefault();
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export default function Navbar() {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toggle = (
    <button
      data-testid="language-toggle-button"
      onClick={() => setLang(lang === "en" ? "ar" : "en")}
      className="flex items-center gap-2 border border-gold/40 px-3 py-1.5 text-[11px] tracking-[0.15em] text-gold transition-colors duration-300 hover:bg-gold/10"
      aria-label="Switch language"
    >
      <Translate size={14} weight="duotone" />
      {lang === "en" ? "العربية" : "EN"}
    </button>
  );

  return (
    <header
      data-testid="main-navigation"
      className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-xl transition-colors duration-500 ${
        scrolled ? "border-gold/30 bg-charcoal/85" : "border-gold/15 bg-charcoal/60"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-12">
        <a
          href="#top"
          data-testid="nav-logo"
          onClick={(e) => scrollToSection(e, "#hero")}
          className="font-serif text-lg uppercase tracking-[0.3em] text-[#FAFAFA] transition-colors duration-300 hover:text-gold"
        >
          {t.brand.first} <span className="text-gold">{t.brand.last}</span>
        </a>
        <div className="hidden items-center gap-8 lg:flex">
          <ul className="flex items-center gap-8">
            {SECTIONS.map((s) => (
              <li key={s}>
                <a
                  href={`#${s}`}
                  data-testid={`nav-link-${s}`}
                  onClick={(e) => scrollToSection(e, `#${s}`)}
                  className="text-xs uppercase tracking-[0.25em] text-[#A3A3A3] transition-colors duration-300 hover:text-gold"
                >
                  {t.nav[s]}
                </a>
              </li>
            ))}
          </ul>
          {toggle}
        </div>
        <div className="flex items-center gap-3 lg:hidden">
          {toggle}
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="text-gold"
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <List size={26} />}
          </button>
        </div>
      </nav>
      <AnimatePresence>
        {open && (
          <motion.ul
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35 }}
            className="overflow-hidden border-t border-gold/15 bg-charcoal/95 backdrop-blur-xl lg:hidden"
          >
            {SECTIONS.map((s) => (
              <li key={s} className="border-b border-gold/10">
                <a
                  href={`#${s}`}
                  data-testid={`nav-mobile-link-${s}`}
                  onClick={(e) => {
                    setOpen(false);
                    scrollToSection(e, `#${s}`);
                  }}
                  className="block px-6 py-4 text-sm uppercase tracking-[0.25em] text-[#A3A3A3] transition-colors duration-300 hover:text-gold"
                >
                  {t.nav[s]}
                </a>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </header>
  );
}
